import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { talentsAPI } from "../../../api/talentApi";
import { adminAPI } from "../../admin/services/admin.api";
import { EMPTY_FORM } from "../types/register-talent.types";
import type { TalentFormData, TalentFormErrors } from "../types/register-talent.types";
import { validateBasicInfo, validateTalentDetails, calculateAge, buildRegistrationPayload, TALENT_CATEGORIES, fieldValidators } from "../utils/register-talent.utils";
import { useAuth } from "../../../context/AuthContext";
import { usePhoneCheck } from "../../../shared/hooks/usePhoneCheck";
import { useEmailCheck } from "../../../shared/hooks/useEmailCheck";
import { extractFieldErrors } from "../../../api/api";
import type { Question } from "../../admin/hooks/useQuestionsManagement";
import { useLanguage } from "../../../context/LanguageContext";

// register-talent.utils.ts's validators return translation KEYS (it's a
// plain function module, no hook context) — translate every value in an
// errors map here, where t() is available. `measurements` is the one
// composite field that can carry two keys joined by " / " (both height and
// weight invalid at once), so each half is translated separately.
function translateErrors<T extends Record<string, string | undefined>>(
  errors: T,
  t: (key: string) => string,
): T {
  const translated = {} as T;
  for (const [field, value] of Object.entries(errors)) {
    if (!value) continue;
    (translated as any)[field] = value.includes(" / ")
      ? value.split(" / ").map((k) => t(k)).join(" / ")
      : t(value);
  }
  return translated;
}

// 3 visible steps: 1=BasicInfo, 2=TalentDetails+Questions, 3=Gallery
export function useRegisterTalent() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const { t } = useLanguage();

  const [formData, setFormData] = useState<TalentFormData>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<TalentFormErrors>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [talentInterests, setTalentInterests] = useState<string[] | null>(null);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [computedAge, setComputedAge] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [error, setError] = useState("");
  const [specialtiesOptions, setSpecialtiesOptions] = useState<
    { id: number; name: string; options?: { id: number; name: string }[] }[]
  >([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionAnswers, setQuestionAnswers] = useState<Record<number, boolean>>({});
  const [profileImage, setProfileImage] = useState<File | null>(null);

  const { phoneExists, checkingPhone } = usePhoneCheck(formData.phone);
  const { emailExists, checkingEmail } = useEmailCheck(formData.email);

  // Computed once per formData change, shared by TalentDetailsStep's live
  // preview and handleTalentDetailsSubmit's gate below — previously each ran
  // its own validateTalentDetails(formData) call on every render/submit.
  const talentDetailsErrors = useMemo(
    () => translateErrors(validateTalentDetails(formData), t),
    [formData, t],
  );

  useEffect(() => {
    talentsAPI.getSpecialties()
      .then((res) => setSpecialtiesOptions(res.data ?? []))
      .catch(() => {});

    setQuestionsLoading(true);
    adminAPI.getQuestions()
      .then((res) => {
        const all: Question[] = res.data?.data ?? res.data ?? res ?? [];
        const filtered = all.filter((q) => q.is_active);
        setQuestions(filtered);
      })
      .catch((err) => {
        console.error("[DEBUG] Error fetching questions:", err);
      })
      .finally(() => setQuestionsLoading(false));
  }, []);

  const handleGalleryChange = (files: File[]) =>
    setFormData((prev) => ({ ...prev, gallery: files }));

  const handleProfileImageChange = (file: File | null) =>
    setProfileImage(file);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    setError("");
  };

  // Validate a single field on blur and show its error immediately
  const handleBlur = (field: keyof TalentFormData) => {
    const err = fieldValidators[field]?.(formData[field], formData);
    setFormErrors((prev) => ({ ...prev, [field]: err ? t(err) : err }));
  };

  const handleSocialMediaChange = (
    platform: keyof TalentFormData["socialMedia"],
    value: string,
  ) => setFormData((prev) => ({ ...prev, socialMedia: { ...prev.socialMedia, [platform]: value } }));

  const handleMeasurementsChange = (
    field: keyof TalentFormData["measurements"],
    value: string,
  ) => setFormData((prev) => ({ ...prev, measurements: { ...prev.measurements, [field]: value } }));

  const updateCategoryGroup = (categoryName: string, values: string[]) => {
    const cat = TALENT_CATEGORIES.find((c) => c.category === categoryName);
    const groupValues = (cat?.options ?? []).map((o) =>
      typeof o === "object" ? o.value : o
    );
    setFormData((prev) => ({
      ...prev,
      categories: [...prev.categories.filter((v) => !groupValues.includes(v)), ...values],
    }));
  };

  const handleDateOfBirthChange = (value: string) => {
    setComputedAge(value ? calculateAge(value) : null);
    setFormData((prev) => ({ ...prev, date_of_birth: value }));
  };

  const toggleInterest = (interest: string) => setSelectedInterests([interest]);

  // Step 1 → Step 2
  const handleBasicInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = translateErrors(validateBasicInfo(formData), t);
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    if (phoneExists || checkingPhone) return;
    if (emailExists || checkingEmail) return;
    setFormErrors({}); // Step 1 passed — clear so its errors can't resurface later
    setCurrentStep(2);
  };

  // Step 2 → Step 3 (talent details + questions combined)
  const handleTalentDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(talentDetailsErrors).length) { setFormErrors(talentDetailsErrors); return; }
    // Validate all active questions are answered
    const unanswered = questions.filter((q) => !(q.id in questionAnswers));
    if (unanswered.length > 0) {
      setError(t("auth.validation.answerAllQuestions"));
      return;
    }
    setError("");
    setFormErrors({}); // Step 2 passed — clear so its errors can't resurface later
    setCurrentStep(3);
  };

  const handleAnswerQuestion = (id: number, value: boolean) =>
    setQuestionAnswers((prev) => ({ ...prev, [id]: value }));

  // Step 3 → Submit
  const handleGallerySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.gallery.length < 6) return;
    if (!profileImage) {
      setError(t("auth.validation.uploadProfilePhotoRequired"));
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const payload = buildRegistrationPayload(formData, talentInterests || []);

      // Append question answers before files
      payload.append("question_answers", JSON.stringify(questionAnswers));

      // Append profile image if provided
      if (profileImage) {
        payload.append("profile_image", profileImage);
      }

      // Append gallery files
      formData.gallery.forEach((file) => {
        payload.append("gallery[]", file);
      });
      

      const res = await talentsAPI.registerTalent(payload);
      const token = res.data?.token ?? res.token;
      const user = res.data?.user ?? res.user;
      if (token && user) setSession(user, token);
      navigate("/dashboard/talent");
    } catch (err: any) {
      const backendFieldErrors = extractFieldErrors(err) as TalentFormErrors;
      if (Object.keys(backendFieldErrors).length) {
        setFormErrors((prev) => ({ ...prev, ...backendFieldErrors }));
        const step1Fields: (keyof TalentFormData)[] = ["first_name", "last_name", "email", "phone", "password", "confirmPassword"];
        if (step1Fields.some((f) => backendFieldErrors[f])) setCurrentStep(1);
        else setCurrentStep(2);
      }
      setError(err?.message || t("auth.validation.registrationFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Step guard: only allow advancing, never skipping
  const goToStep = (step: number) => {
    if (step <= currentStep + 1) setCurrentStep(step);
  };

  return {
    formData, formErrors, talentDetailsErrors, currentStep, setCurrentStep: goToStep,
    talentInterests, setTalentInterests, selectedInterests, toggleInterest,
    computedAge, isLoading, questionsLoading, error, specialtiesOptions,
    phoneExists, checkingPhone, emailExists, checkingEmail,
    questions, questionAnswers,
    profileImage,
    handleChange, handleGalleryChange, handleProfileImageChange,
    handleBlur,
    handleSocialMediaChange, handleMeasurementsChange,
    updateCategoryGroup, handleDateOfBirthChange,
    handleBasicInfoSubmit, handleTalentDetailsSubmit,
    handleAnswerQuestion, handleGallerySubmit,
  };
}
